import { ChevronDown, ChevronRight, Lock, X } from 'lucide-react'
import { useState } from 'react'

import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { rollUpTopicMastery, type TopicBucket } from './_lib/derivations'
import { KnowledgeMapSnapshot } from './knowledge-map-snapshot'
import { RecalculatingBanner } from './recalculating-banner'
import { useRecommendationEngineMock } from './recommendation-engine-context'

/** Picks a bar color from how a mastery value compares to its target threshold. */
function masteryBarClass(mastery: number, threshold: number): string {
  if (mastery >= threshold) return 'bg-emerald-500'
  if (mastery >= threshold * 0.6) return 'bg-amber-500'
  return 'bg-rose-500'
}

const BUCKET_LABEL: Record<TopicBucket, string> = {
  mastered: 'Mastered',
  proficient: 'Proficient',
  learning: 'Learning',
  remaining: 'Remaining',
}

function BucketBox({
  bucket,
  count,
  active,
  onClick,
  className,
}: {
  bucket: TopicBucket
  count: number
  active: boolean
  onClick: () => void
  className: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Filter to ${BUCKET_LABEL[bucket]}`}
      className={cn(
        'rounded-lg p-3 text-center transition-all',
        className,
        active && 'ring-primary ring-2',
      )}
    >
      <p className="text-foreground text-xl font-bold">{count}</p>
      <p className="text-muted-foreground text-xs">{BUCKET_LABEL[bucket]}</p>
    </button>
  )
}

/**
 * Progress view — overall progress with bucket-as-filter drill-down into the
 * per-topic breakdown, plus an expandable subtopic view showing the FSRS state
 * behind each number. All data comes from the shared mock store.
 */
export function ProgressView() {
  const { topics, overallMastery, bucketCounts, bucketOf } =
    useRecommendationEngineMock()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState<TopicBucket | null>(null)

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const toggleFilter = (bucket: TopicBucket) =>
    setFilter((prev) => (prev === bucket ? null : bucket))

  const visibleTopics = topics.filter(
    (t) => filter === null || bucketOf(t.id) === filter,
  )

  return (
    <div className="space-y-6">
      <RecalculatingBanner />
      {/* Total progress */}
      <div className="border-border rounded-lg border p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-foreground text-sm font-semibold">
            Total Progress
          </h3>
          <button
            type="button"
            onClick={() => setFilter('mastered')}
            aria-label="Show mastered topics"
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            overall mastery{' '}
            <span className="text-foreground font-semibold">
              {overallMastery}
            </span>
          </button>
        </div>
        <Progress value={overallMastery} barClassName="bg-primary" size="lg" />
        <div className="mt-4 grid grid-cols-4 gap-3">
          <BucketBox
            bucket="mastered"
            count={bucketCounts.mastered}
            active={filter === 'mastered'}
            onClick={() => toggleFilter('mastered')}
            className="bg-emerald-500/10"
          />
          <BucketBox
            bucket="proficient"
            count={bucketCounts.proficient}
            active={filter === 'proficient'}
            onClick={() => toggleFilter('proficient')}
            className="bg-cyan-500/10"
          />
          <BucketBox
            bucket="learning"
            count={bucketCounts.learning}
            active={filter === 'learning'}
            onClick={() => toggleFilter('learning')}
            className="bg-amber-500/10"
          />
          <BucketBox
            bucket="remaining"
            count={bucketCounts.remaining}
            active={filter === 'remaining'}
            onClick={() => toggleFilter('remaining')}
            className="bg-muted"
          />
        </div>
      </div>

      {/* Topic breakdown */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-foreground text-sm font-semibold">
            Topic Breakdown
          </h3>
          {filter && (
            <button
              type="button"
              onClick={() => setFilter(null)}
              aria-label="Clear filter"
              className="bg-muted text-muted-foreground hover:text-foreground flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
            >
              Showing: {BUCKET_LABEL[filter]}
              <X size={11} />
            </button>
          )}
        </div>
        <p className="text-muted-foreground mb-3 text-xs">
          Topic mastery is a weakness-weighted rollup of its subtopics. Expand a
          topic to see the FSRS state behind it.
        </p>
        <div className="space-y-2">
          {visibleTopics.map((topic) => {
            const isOpen = expanded[topic.id] ?? false
            const topicMastery = rollUpTopicMastery(topic.subtopics, topic)
            return (
              <div
                key={topic.id}
                className="border-border overflow-hidden rounded-lg border"
              >
                <button
                  type="button"
                  onClick={() => toggle(topic.id)}
                  aria-label={`Toggle ${topic.name} subtopics`}
                  className="hover:bg-muted/50 flex w-full items-center gap-3 p-3 text-left transition-colors"
                >
                  {isOpen ? (
                    <ChevronDown
                      size={16}
                      className="text-muted-foreground shrink-0"
                    />
                  ) : (
                    <ChevronRight
                      size={16}
                      className="text-muted-foreground shrink-0"
                    />
                  )}
                  <div className="w-44 shrink-0">
                    <p className="text-foreground flex items-center gap-1.5 truncate text-sm font-medium">
                      {topic.name}
                      {!topic.prerequisitesMet && (
                        <Lock
                          size={12}
                          className="text-muted-foreground shrink-0"
                        />
                      )}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {topic.subtopics.length} subtopics
                    </p>
                  </div>
                  <div className="flex-1">
                    <Progress
                      value={topicMastery}
                      barClassName={masteryBarClass(
                        topicMastery,
                        topic.maxProficiencyThreshold,
                      )}
                    />
                  </div>
                  <div className="w-24 shrink-0 text-right">
                    <span className="text-foreground text-sm font-semibold tabular-nums">
                      {topicMastery}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {' '}
                      / {topic.maxProficiencyThreshold}
                    </span>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-border space-y-2 border-t p-3">
                    {!topic.prerequisitesMet && (
                      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <Lock size={12} />
                        Prerequisites not yet met — this topic is gated out of
                        recommendations.
                      </p>
                    )}
                    <KnowledgeMapSnapshot scope="topic" topicId={topic.id} />
                  </div>
                )}
              </div>
            )
          })}
          {visibleTopics.length === 0 && (
            <div className="text-muted-foreground border-border rounded-lg border border-dashed py-10 text-center text-sm">
              No topics in this bucket.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
